import { mapGetters } from 'vuex'
import { charset7bit } from '@/config/charsets.js'
import { getDefaultVariablesTitle, getSubvariablesTitle } from '@/modules/maria/helpers/variables.js'

export const variablesInspection = {
  props: {
    message: {
      type: String,
      default: '',
    },
    offerType: {
      type: String,
      default: 'offer_sales',
      validator: (value) =>
        [
          'offer_sales',
          'percentage_offer_sales',
          'cashback_absolute_offer_sales',
          'cashback_percentage_offer_sales',
          'takepay_offer_sales',
          'points_offer_sales',
          'combo_offer_sales',
        ].includes(value),
    },
    channel: {
      type: String,
      required: true,
      validator: (value) => ['sms', 'push', 'whatsapp', 'email'].includes(value),
    },
    field: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'title', 'inner_message_title', 'inner_message_text'].includes(value),
    },
    insertBestOfferAsDiv: {
      type: Boolean,
      default: false,
    },
    isInspectionInsideBestOffer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    parsedMessage: '',
    BEST_OFFER_PREFIX: 'Melhor oferta ',
  }),
  computed: {
    ...mapGetters(['campaign', 'campaignVariablesConfiguration']),
  },
  watch: {
    message() {
      this.formatMessage()
    },
    campaignVariablesConfiguration() {
      this.formatMessage()
    },
    offerType() {
      this.formatMessage()
    },
  },
  mounted() {
    this.formatMessage()
  },
  methods: {
    formatMessage() {
      this.parsedMessage = this.formatTextWithStyles(this.message, this.isInspectionInsideBestOffer ? getSubvariablesTitle() : getDefaultVariablesTitle())
    },
    formatTextWithinBestOffer(text, allowedVariables) {
      let htmlContent = this.formatTextWithStyles(text, allowedVariables, true)
      if (htmlContent.length > 0) {
        const wrapperTag = this.insertBestOfferAsDiv ? 'div' : 'span'
        const wrapperClass = this.insertBestOfferAsDiv ? ' class="best-offer-bg"' : ''

        htmlContent = `<${wrapperTag}${wrapperClass}><span class="inspection-text variable-style">[${this.BEST_OFFER_PREFIX}</span>${htmlContent}<span class="inspection-text variable-style">]</span></${wrapperTag}>`
      }
      return htmlContent
    },
    formatTextWithStyles(text, allowedVariables, isFormattingInsideBestOffer = false) {
      const normalCharSet = new Set(charset7bit)

      // Regex combinando:
      // - Subvariáveis: {subvar}
      // - Variáveis: [var]
      // - Espaços (\s)
      // - Demais caracteres (incluindo quebras de linha)
      const mainRegex = /(\{[^}]+\}|\[[^\]]+\]|\s|[\S\s])/g

      const highlight = (char) => {
        if (!normalCharSet.has(char)) {
          return `<span class="inspection-text special-char-style">${char}</span>`
        } else {
          return `<span class="inspection-text char-style">${char}</span>`
        }
      }

      const htmlContent = text.replace(mainRegex, (match) => {
        const isSubVar = /^\{[A-Za-zÀ-ÖØ-öø-ÿ0-9_\s/.,:()-]+(?:_[A-Za-z])?\}$/.test(match)
        const isVar = /^\[[^\]]+\]$/.test(match)
        const isWhiteSpace = /^\s$/.test(match)
        const bestOfferVarRegex = /\[(10|[1-9]) melhor oferta\]/i
        const bestOfferVarMatch = match.match(bestOfferVarRegex)

        if (!isFormattingInsideBestOffer && isVar && bestOfferVarMatch) {
          const bestOfferMessage = this.getBestOfferMessage(bestOfferVarMatch[1])
          return this.formatTextWithinBestOffer(bestOfferMessage, getSubvariablesTitle())
        } else if (isSubVar || isVar) {
          const normalizedMatch = match.replace(/(_[A-Za-z])(?=\}$)/, '')
          if (allowedVariables.includes(normalizedMatch)) {
            const useSubVarStyle = isSubVar || (isVar && isFormattingInsideBestOffer)
            return `<span class="inspection-text ${useSubVarStyle ? 'subvariable-style' : 'variable-style'}">${match}</span>`
          } else {
            return match.split('').map(highlight).join('')
          }
        } else if (match.startsWith('{') || match.startsWith('[')) {
          return match.split('').map(highlight).join('')
        } else if (isWhiteSpace) {
          if (match === '\n') {
            return `<br class="inspection-text space-style">`
          } else {
            return `<span class="inspection-text space-style">&nbsp;</span>`
          }
        } else {
          return highlight(match)
        }
      })

      return htmlContent
    },
    getBestOfferMessage(stringAmount) {
      const bestOffer = this.campaignVariablesConfiguration?.[this.channel]?.[this.field]?.[this.offerType]
      const amount = parseInt(stringAmount, 10)
      if (!bestOffer) {
        return ''
      } else if (amount === 1 || amount > 10) {
        return (bestOffer?.repeatable_text ?? '') + (bestOffer?.validity_text ?? '')
      }

      const A_CODE = 'A'.charCodeAt(0)
      let message = Array.from({ length: amount }, (_, i) => {
        const suffix = String.fromCharCode(A_CODE + i)
        return bestOffer?.repeatable_text?.replace(/\{([^}]+)\}/g, (_, inner) => `{${inner}_${suffix}}`) ?? ''
      }).join(', ')

      message += bestOffer?.validity_text ?? ''
      return message
    },
  },
}
